import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@theme/useTheme';

interface InputBoxWithUnitProps {
  beforeText: string;
  maxLength?: number;
  borderColor?: string;
  isSecureText: boolean;
  value: string;
  unitValue: string;
  autoFocus?: boolean;
  onFocusChange?: (isInputFocused: boolean) => void;
  onChange: (text: string) => void;
  onChangeExtra?: (text: string) => void;
  description?: string;
  keyBoardType?: 'text' | 'numeric' | 'email' | 'tel';
  hasError?: boolean;
  errorMessage?:string;
  required?: boolean;
  placeholder?: string;
  min?:number;
  max?:number;
}

const InputBoxWithUnit: React.FC<InputBoxWithUnitProps> = ({
  beforeText,
  maxLength,
  isSecureText,
  value,
  unitValue,
  autoFocus = false,
  onFocusChange,
  onChange,
  onChangeExtra,
  description,
  keyBoardType = 'text',
  hasError = false,
  errorMessage='',
  required = false,
  placeholder,
  min = 0,
  max
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { colors } = useTheme();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocusChange?.(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    onFocusChange?.(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    onChangeExtra?.(newValue);
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (keyBoardType === 'numeric') {
      e.currentTarget.blur();
    }
  };

  const getInputType = () => {
    if (isSecureText) return 'password';
    if (keyBoardType === 'numeric') return 'number';
    if (keyBoardType === 'email') return 'email';
    if (keyBoardType === 'tel') return 'tel';
    return 'text';
  };

  const getBorderColor = () => {
    if (hasError) return colors.error;
    if (isFocused || value) return colors.primary; // Primary color when focused or has value
    return colors.border; // Theme border when not focused and empty
  };

  return (
    <div className="w-full">
      {/* Label */}
      <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
        {beforeText}
        {required && <span className="ml-1" style={{ color: colors.error }}>*</span>}
      </label>

      {/* Input Field with Unit Selection */}
      <div className="relative flex">
        <input
          ref={inputRef}
          type={getInputType()}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          maxLength={maxLength}
          placeholder={placeholder}
          min={min}
          max={max}
          className="flex-1 px-3 py-2 rounded-l-lg border-2 border-r-0 focus:outline-none text-sm pr-0"
          style={{
            backgroundColor: colors.input,
            borderColor: getBorderColor(),
            color: colors.text,
            minHeight: '44px', // Touch-friendly minimum height
            transition: 'border-color 200ms ease-in-out',
          }}
        />

        {/* Unit Display (Static) */}
        <div
          className="px-3 py-2 rounded-r-lg border-2 border-l-0 flex items-center text-xs font-medium"
          style={{
            backgroundColor: colors.input,
            borderColor: getBorderColor(),
            color: colors.text,
            minHeight: '44px',
            transition: 'border-color 200ms ease-in-out',
          }}
          aria-label={`${beforeText} unit`}
        >
          {unitValue === 'lbs' ? 'lbs' : unitValue}
        </div>
      </div>

      {!hasError && description && (
        <p className="mt-1 text-xs" style={{ color: colors.text }}>
          {description}
        </p>
      )}

      {hasError && (
        <p className="mt-1 text-xs" style={{ color: colors.error }}>
          {errorMessage || "This field is required"}
        </p>
      )}
    </div>
  );
};

export default InputBoxWithUnit;
